import React, { useRef, useEffect, useState, useMemo } from "react";
import "./index.css";
import { useLocation } from "react-router-dom";
import Loader from "../../../components/loader";
import {initDevice} from "../../../utils";
import {applyFocusSettings, getBestBackCamera, getOptimizedConstraints} from "../../../utils/camera-utils";
import {getVideoInputs, onReportEvent} from "../../cardCapture/utils";
import {SDK_EVENT_KEYS} from "../../../utils/logService";

export function WrapperTestCamera() {
    const location = useLocation();
    const [config, setConfig] = useState({});
    const [configLoading, setLoading] = useState(true);

    const projectName = useMemo(() => {
        const params = new Proxy(new URLSearchParams(location.search), {
            get: (searchParams, prop) => searchParams.get(prop),
        });

        const name = params.projectName || "Default";

        return name;
    }, [location.search]);

    const url = () => {
        if (location.pathname.includes("card-capture")) {
            return `https://lnesv3-conf.ado-tech.com/${projectName}/CardCapture/config.json`;
        }
        return `https://lnesv3-conf.ado-tech.com/${projectName}/Liveness/config.json`;
    };

    const defaultUrl = () => {
        if (location.pathname.includes("card-capture")) {
            return `https://lnesv3-conf.ado-tech.com/Default/CardCapture/config.json`;
        }
        return `https://lnesv3-conf.ado-tech.com/Default/Liveness/config.json`;
    };

    function setStyles(stylesObject) {
        const root = document.documentElement;
        root.style.setProperty("--background-color", stylesObject.backgroundColor);
        root.style.setProperty("--message-color", stylesObject.messageColor);
        root.style.setProperty("--message-back-color", stylesObject.messageBackgroundColor);
        root.style.setProperty("--guide-color", stylesObject.guideColor);
        root.style.setProperty("--success-color", stylesObject.successColor);
        root.style.setProperty("--error-color", stylesObject.errorColor);
        root.style.setProperty("--message-error-color", stylesObject.messageErrorColor);
        root.style.setProperty("--font-size", stylesObject.fontSize);
    }

    const fetchConfig = async () => {
        try {
            const response = await fetch(url(), { method: "GET" });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            setConfig(data);
            setStyles(data.styles);
        } catch (error) {
            const response = await fetch(defaultUrl(), { method: "GET" });
            const data = await response.json();
            setConfig(data);
            setStyles(data.styles);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    if (configLoading) {
        return null;
    }

    return <CardCaptureV7 configLoading={configLoading} configValue={config} />;
}

export default function CardCaptureV7({ configValue, configLoading }) {
    const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
    });

    const containerAdo = useRef(null);
    const videoRef = useRef(null);
    const guideAdo = useRef(null);
    const canvasRef = useRef(null);
    const textPreview = useRef(null);
    const textPreviewBack = useRef(null);

    const isFront = params?.isFront || 'true';
    const platform = params.platform || 'web';

    const [loading, setLoading] = useState(true);
    const [emitReadyCam, setEmitReadyCam] = useState("false");
    const [isChangingCamera, setIsChangingCamera] = useState(false);
    const [stream, setStream] = useState(null);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [availableCameras, setAvailableCameras] = useState([]);
    const [selectedCamera, setSelectedCamera] = useState("");
    const [activeDeviceId, setActiveDeviceId] = useState("");
    const [defaultDeviceId, setDefaultDeviceId] = useState(null);

    // Nuevos estados para información del dispositivo
    const [deviceInfo, setDeviceInfo] = useState(null);
    const [deviceInfoStatus, setDeviceInfoStatus] = useState('loading');

    function isMovil() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }

    // Función para obtener modelo técnico del dispositivo
    const obtenerModeloTecnicoDispositivo = async () => {
        // Verificar si el navegador soporta User-Agent Client Hints (UA-CH)
        if (!navigator.userAgentData) {
            setDeviceInfoStatus('not-supported');
            return null;
        }

        const hints = ['model', 'platform', 'platformVersion'];

        try {
            const data = await navigator.userAgentData.getHighEntropyValues(hints);

            const modelo = data?.model;
            const plataforma = data?.platform;
            const versionSO = data?.platformVersion;
            const marca = data?.brands ? (data?.brands[0]?.brand || 'N/A') : 'N/A';

            const info = {
                modelo,
                plataforma,
                versionSO,
                marca
            };

            if (modelo && modelo !== '') {
                setDeviceInfoStatus('success');
                setDeviceInfo(info);
                return info;
            } else {
                setDeviceInfoStatus('no-model');
                setDeviceInfo(info);
                return info;
            }

        } catch (error) {
            console.error('Error al solicitar Client Hints:', error);
            setDeviceInfoStatus('error');
            return null;
        }
    };

    // Obtener lista de cámaras disponibles
    const getAvailableCameras = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');

            // Filtrar solo cámaras traseras (environment)
            const backCameras = videoDevices.filter(device => {
                const label = device.label.toLowerCase();
                return label.includes('back') ||
                    label.includes('rear') ||
                    label.includes('trasera') ||
                    label.includes('environment') ||
                    !label.includes('front') && !label.includes('user') && !label.includes('frontal');
            });

            // Si no se pueden identificar las traseras por el label, usar todas las disponibles
            const camerasToUse = backCameras.length > 0 ? backCameras : videoDevices;

            setAvailableCameras(camerasToUse);

            return camerasToUse;
        } catch (error) {
            console.error('Error al obtener cámaras:', error);
            return [];
        }
    };

    // Función para animar la aparición de elementos
    const showElements = () => {
        if (guideAdo.current) {
            guideAdo.current.style.transition = 'transform 0.5s ease, opacity 0.5s ease';
            guideAdo.current.style.transform = 'scale(1)';
            guideAdo.current.style.opacity = '1';
        }

        if (videoRef.current) {
            videoRef.current.style.transition = 'opacity 0.5s ease';
            videoRef.current.style.opacity = '1';
        }
    };

    // Solicitar permisos y inicializar cámara
    const initializeCamera = async (deviceId = null) => {
        try {
            setLoading(true);
            setPermissionDenied(false);

            // Detener stream anterior si existe
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }

            const deviceInfo = initDevice();

            // Si se proporciona un deviceId específico, usarlo
            let constraints;
            console.log('DeviceId a usar:', deviceId);
            if (deviceId) {
                constraints = await getOptimizedConstraints(deviceInfo, 'environment');
                constraints.video.deviceId = { exact: deviceId }
            } else {
                constraints = await getOptimizedConstraints(deviceInfo, 'environment');
            }

            let currentStream = await navigator.mediaDevices.getUserMedia(constraints);

            // Solo buscar la mejor cámara si NO se especificó un deviceId
            if (!deviceId) {
                const model = await obtenerModeloTecnicoDispositivo();
                const bestCamera = await getBestBackCamera(model?.modelo);
                const currentDeviceId = currentStream.getVideoTracks()[0].getSettings().deviceId;
                setDefaultDeviceId(bestCamera);
                if (bestCamera?.deviceId && bestCamera.deviceId !== currentDeviceId) {
                    currentStream.getVideoTracks()[0].stop();
                    const optimizedConstraints = await getOptimizedConstraints(deviceInfo, 'environment');
                    optimizedConstraints.video.deviceId = bestCamera?.deviceId;
                    currentStream = await navigator.mediaDevices.getUserMedia(optimizedConstraints);
                }
            }

            const mediaStream = currentStream;

            // Obtener el deviceId del stream activo
            const activeDevice = mediaStream.getVideoTracks()[0].getSettings().deviceId;
            console.log('Active camera deviceId:', activeDevice);

            setStream(mediaStream);
            setActiveDeviceId(activeDevice);
            setSelectedCamera(activeDevice);

            await getAvailableCameras();
            await applyFocusSettings(currentStream);

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;

                await new Promise((resolve) => {
                    if (videoRef.current) {
                        videoRef.current.onloadedmetadata = () => {
                            videoRef.current?.play();
                            resolve();
                        };
                    }
                });

                // Pequeño delay para asegurar que todo está cargado y mostrar elementos
                setTimeout(() => {
                    setEmitReadyCam("true");
                    setLoading(false);
                    setIsChangingCamera(false);
                    showElements();
                }, 300);
            }

        } catch (error) {
            console.error('Error al acceder a la cámara:', error);
            setLoading(false);
            setPermissionDenied(true);
            setEmitReadyCam("false");

            // Manejar diferentes tipos de errores
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                alert('Permiso de cámara denegado. Por favor, habilita el acceso a la cámara en la configuración de tu navegador.');
            } else if (error.name === 'NotFoundError') {
                alert('No se encontró ninguna cámara en el dispositivo.');
            } else if (error.name === 'NotReadableError') {
                alert('La cámara está siendo usada por otra aplicación.');
            } else {
                alert('Error al acceder a la cámara: ' + error.message);
            }
        }
    };

    // Cambiar de cámara
    const handleCameraChange = async (e) => {
        const newDeviceId = e.target.value;

        if (newDeviceId === activeDeviceId) {
            return;
        }

        setSelectedCamera(newDeviceId);
        setIsChangingCamera(true);

        if (guideAdo.current) {
            guideAdo.current.style.opacity = '0';
        }
        if (videoRef.current) {
            videoRef.current.style.opacity = '0';
        }

        setTimeout(() => {
            initializeCamera(newDeviceId);
        }, 300);
    };

    // Inicializar cámara y obtener info del dispositivo al montar el componente
    useEffect(() => {
        if (!configLoading) {
            initializeCamera();
        }

        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [configLoading]);

    // Debug: mostrar información de la cámara activa
    useEffect(() => {
        if (activeDeviceId && availableCameras.length > 0) {
            const activeCamera = availableCameras.find(cam => cam.deviceId === activeDeviceId);
            console.log('Active camera:', activeCamera?.label || activeDeviceId);
        }
    }, [activeDeviceId, availableCameras]);

    return (
        <div
            ref={containerAdo}
            className={`container v6 movil background-default`}
        >
            {/* Loader mientras se piden permisos O mientras se inicializa la cámara */}
            {loading && emitReadyCam !== "true" && (
                <div className="loader-container">
                    <Loader color="#FFF" primary="#FF3D00" />
                </div>
            )}

            {/* Selector de cámara */}
            {emitReadyCam === "true" && availableCameras.length > 1 && (
                <div className="camera-selector-container">
                    <select
                        className="camera-selector"
                        value={selectedCamera}
                        onChange={handleCameraChange}
                        disabled={isChangingCamera}
                    >
                        {availableCameras.map((camera, index) => (
                            <option key={camera.deviceId} value={camera.deviceId}>
                                {camera.label || `Cámara ${index + 1}`}
                                {camera.deviceId === activeDeviceId && ' (Activa)'}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Mensaje de error si se deniegan permisos */}
            {permissionDenied && !loading && (
                <div className="permission-denied-container">
                    <h3>Permiso de cámara necesario</h3>
                    <p>Para continuar, necesitamos acceso a tu cámara.</p>
                    <button onClick={() => initializeCamera()}>
                        Intentar nuevamente
                    </button>
                </div>
            )}

            {/* Contenido principal */}
            {!permissionDenied && (
                <>
                    <div className={isChangingCamera ? 'hiding' : ''}>
                        <div
                            ref={guideAdo}
                            className={`guide-ado ${platform === "web" ? "margin-top-web" : ""} ${
                                isMovil() ? "movil" : "desk"
                            }`}
                        >
                            <div className="dashed-marked" />
                            <video
                                id="video-camera"
                                className={`container-camera-movil video-movil ${isMovil() ? "movil" : "desk"}`}
                                ref={videoRef}
                                controls={false}
                                autoPlay
                                playsInline
                                muted
                                disablePictureInPicture
                                disableRemotePlayback
                                style={{
                                    WebkitAppearance: "none",
                                    appearance: "none",
                                    pointerEvents: "auto"
                                }}
                            />
                            {isFront === "true" ? (
                                <div className="type-capture-1">
                                    <div ref={textPreview} className="text-preview text-guide">
                                        Ubica la parte de adelante de tu documento
                                    </div>
                                </div>
                            ) : (
                                <div className="type-capture-2">
                                    <div ref={textPreviewBack} className="text-preview-back text-guide">
                                        Ubica la parte trasera de tu documento
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {defaultDeviceId && (
                        <div className="initial-camera-info">
                            <div className="initial-camera-badge">
                                <span>Información</span>
                            </div>
                            <div className="initial-camera-label">
                                <div>
                                    <div className="device-platform">Cámara</div>
                                    <div className="device-model">{defaultDeviceId.label}</div>
                                </div>
                                {deviceInfo && (
                                    <>
                                        <div>
                                            <div className="device-platform">Modelo</div>
                                            <div className="device-model">{deviceInfo?.modelo}</div>
                                        </div>
                                        <div>
                                            <div className="device-model">Version</div>
                                            <div className="device-platform">{deviceInfo?.plataforma} {deviceInfo?.versionSO}</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    <canvas id="canvas-view" className="canvas-view" ref={canvasRef} />
                    <img alt="imgResult" id="imgResult" className="img-result" />
                    <div id="message-alert-ado" className="message-alert-ado">
                        <h2>Error al validar el documento</h2>
                        <p>Verifique que el documento se vea correctamente</p>
                    </div>
                </>
            )}
        </div>
    );
}
